import React from "react";
import { Link } from "react-router-dom";

const IMG_HERO = "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/qc8xdqm5_Infused%20with%20Himalayan%20Rhubarb%20Cinnamon%20and%20clove%20Tallow%20soap.jpg";

const ingredients = {
    "Grass-fed Tallow": [
        ["Grass-fed Tallow", "The foundation of ALATI. Chosen because it creates a rich, creamy, long-lasting bar. It reflects an old-world soap-making tradition where families used what was available and wasted very little. Used with care, intention and respect for the craft."],
    ],
    "Olive Oil": [
        ["Olive Oil", "Olive oil connects ALATI directly to Greek soap-making tradition. It is the heart of Castile soap and one of the oils we use for slow botanical infusions. It carries the family story from Kalymnos into every batch."],
    ],
    "Coconut Oil": [
        ["Coconut Oil", "Used in selected recipes to support structure, cleansing quality and lather. It is used thoughtfully and balanced carefully within each recipe."],
    ],
    "Castor Oil": [
        ["Castor Oil", "Used in small amounts to support lather and the overall feel of the bar. Part of the quiet detail that goes into a bar feeling beautiful in everyday use."],
    ],
    "Premium Butters": [
        ["Shea Butter", "Included in selected bars for richness and a beautiful finished feel. Used where it supports the structure and character of the recipe."],
        ["Cocoa Butter", "A premium butter used in selected bars. It is expensive, but it adds richness, firmness and a beautiful finished character to a well-made bar."],
        ["Mango Butter", "Available for private orders or custom slabs — one of the premium butters we offer when a customer wants a richer custom bar."],
        ["Aloe Butter", "Available in selected private orders or custom slabs for custom formulations where appropriate."],
    ],
    "Goat Milk": [
        ["Goat Milk", "Used in selected bars to create a creamy, gentle-feeling soap. It connects to traditional farm-based soap making and the use of simple ingredients available from animals and land."],
    ],
    "Botanical Infusions": [
        ["Calendula", "We slowly infuse olive oil with calendula flowers. Over time, the oil gently takes on the warm golden colour of the flowers themselves. Every infusion is slightly different. That is part of the beauty of making soap by hand."],
        ["Basil", "One of the herbs we use in slow oil infusions. It connects with the old-world tradition of using garden and mountain herbs in everyday soap."],
        ["Sage", "Used in selected oil infusions. It belongs to the family of traditional herbs that connect ALATI back to old-world soap making."],
        ["Rosemary", "One of the herbs connected to our family’s soap-making story. It was used in traditional island soap making and remains one of the botanicals we may infuse into olive oil for selected bars."],
        ["Greek Mountain Herbs", "These connect ALATI directly to the island traditions that inspired the brand — the old way of using what the land provided. We infuse them slowly into oil to carry that tradition forward."],
        ["Turmeric", "We infuse turmeric into olive oil for selected turmeric bars. The result is a naturally warm golden colour. Colour should come from real ingredients wherever possible."],
        ["Madder Root", "Used for its beautiful earthy red and terracotta tones. We infuse it slowly so the oil takes on its natural colour before the soap is made. Every batch carries slight variation."],
        ["Himalayan Rhubarb", "Creates soft pink, rose and blush tones in our soaps. One of the botanicals that makes ALATI bars visually distinct — beautiful colour without synthetic colourants."],
        ["Indigo", "Used for its naturally deep blue tones. Creates beautiful variation in our bars, from soft blue through to deeper marbled colour."],
        ["Activated Charcoal", "Gives depth, contrast and boldness to selected bars. Used for colour, contrast, texture and character."],
        ["Cacao", "Gives selected bars a warm earthy colour and grounded character. Creates colour from a real ingredient rather than a synthetic colourant."],
    ],
    "Sea Minerals": [
        ["Kalymnos Sea Salt", "Our signature Sea Mineral Salt Bar uses mineral salt collected by our family from the shores of Kalymnos and surrounding islands of the Dodecanese. This is not a gimmick. It is a genuine family connection to the island."],
    ],
    "Essential Oils": [
        ["Essential Oils Only", "Some ALATI bars are completely unscented. Others use carefully formulated essential oil blends. We do not use synthetic fragrance oils. Essential oils are selected to complement the character of each bar — the scent should feel natural, never overpowering."],
    ],
};

const InsideEveryBar = () => {
    return (
        <div data-testid="ingredients-page" className="bg-[#FAF9F6]">
            <section className="py-20 lg:py-28 px-6 lg:px-10">
                <div className="max-w-4xl mx-auto">
                    <p className="label">Inside Every Bar</p>
                    <h1 className="font-serif text-5xl sm:text-6xl mt-5 leading-[1.04]">
                        Honest ingredients. Nothing unnecessary.
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg text-[#4A4A46] leading-relaxed">
                        Every ingredient in an ALATI bar is here for a reason. This is a quiet
                        guide to what we use, how it&rsquo;s prepared, and why we choose it.
                    </p>
                </div>
            </section>

            <section className="px-6 lg:px-10 pb-20">
                <div className="max-w-5xl mx-auto product-image-wrap aspect-[16/9]">
                    <img src={IMG_HERO} alt="ALATI handcrafted soap bar" className="w-full h-full object-cover" />
                </div>
            </section>

            <section className="px-6 lg:px-10 pb-24">
                <div className="max-w-3xl mx-auto">
                    <p className="text-lg text-[#4A4A46] leading-relaxed italic text-center">
                        Unscented where simplicity matters. Essential oils where scent belongs.
                        Never synthetic fragrance.
                    </p>
                </div>
            </section>

            <section className="px-6 lg:px-10 pb-32" data-testid="ingredients-groups">
                <div className="max-w-4xl mx-auto space-y-20">
                    {Object.entries(ingredients).map(([group, items]) => (
                        <div key={group} data-testid={`group-${group.replace(/\s+/g, "-").toLowerCase()}`}>
                            <h2 className="font-serif text-3xl sm:text-4xl text-[#1C1C1A] leading-tight border-b border-[#E3DED5] pb-5">
                                {group}
                            </h2>
                            <div className="mt-10 space-y-12">
                                {items.map(([name, body]) => (
                                    <div key={name} className="grid grid-cols-1 sm:grid-cols-12 gap-6">
                                        <div className="sm:col-span-4">
                                            <h3 className="font-serif text-2xl text-[#1C1C1A] leading-snug">
                                                {name}
                                            </h3>
                                            <p className="label mt-2 text-[10px]">Why we choose it</p>
                                        </div>
                                        <p className="sm:col-span-8 text-[#4A4A46] leading-relaxed">
                                            {body}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="bg-[#F0EBE1] py-20 px-6 lg:px-10">
                <div className="max-w-3xl mx-auto text-center">
                    <p className="label">Everything has a reason</p>
                    <h2 className="font-serif text-3xl sm:text-4xl mt-5 leading-snug">
                        Nothing is added just for show.
                    </h2>
                    <div className="mt-10 flex flex-wrap gap-3 justify-center">
                        <Link to="/shop" className="btn-primary" data-testid="ingredients-shop-cta">
                            Shop Soap
                        </Link>
                        <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({top: 0, behavior: 'smooth'}); }} className="btn-secondary" data-testid="ingredients-process-cta">
                            Back to the Process
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default InsideEveryBar;
